import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import DownloadIcon from '@mui/icons-material/Download';
import { Tooltip } from '@mui/material';

const MarkDownText = ({ data, img }) => {
  const formattedData = data;

  const containerStyle = {
    maxWidth: '100%',
    padding: '16px',
    boxSizing: 'border-box',
    fontFamily: 'montserrat'
  };

  const markdownStyle = {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    fontFamily: 'montserrat',
    color: 'rgba(0, 0, 0, 0.8)'
  };

  const titleStyle = {
    color: '#336B87',
    fontSize: '30px',
    fontFamily: 'montserrat'
  };

  const subTitleStyle = {
    color: '#336B87',
    fontFamily: 'montserrat'
  };

  const subTitleTextStyle = {
    color: '#336B87',
    fontWeight: 'normal',
    fontFamily: 'montserrat'
  };

  const tableWrapperStyle = {
    borderRadius: '6px',
    overflow: 'hidden'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const tableTheadStyle = {
    fontFamily: 'montserrat',
    backgroundColor: '#90AFC5',
  };

  const tableBodyStyle = {
    fontFamily: 'montserrat',
    backgroundColor: 'white'
  };

  const cellStyle = {
    padding: '8px',
    fontFamily: 'montserrat',
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          .custom-table tr:nth-child(even) {
            background-color: rgba(144, 175, 197, 0.3);
          }
          .custom-table tr:nth-child(odd) {
            background-color: #FFF;
          }
          .custom-table tr:nth-child(even) td:first-child {
            border-top-left-radius: 6px;
            border-bottom-left-radius: 6px;
          }
          .custom-table tr:nth-child(even) td:last-child {
            border-top-right-radius: 6px;
            border-bottom-right-radius: 6px;
          }
        `}
      </style>

      <Markdown
        skipHtml={false}
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={{
          p({ children }) {
            return <p style={markdownStyle}>{children}</p>;
          },
          h1({ children }) {
            return <h1 style={titleStyle}>{children}</h1>;
          },
          h2({ children }) {
            return <h2 style={subTitleStyle}>{children}</h2>;
          },
          h3({ children }) {
            return <h3 style={subTitleTextStyle}>{children}</h3>;
          },
          ul({ children }) {
            return (
              <ul style={{ ...markdownStyle, paddingLeft: '50px', margin: '16px 0', listStyleType: 'disc' }}>
                {children}
              </ul>
            );
          },
          ol({ children }) {
            return (
              <ol style={{ ...markdownStyle, paddingLeft: '50px', margin: '16px 0', listStyleType: 'decimal' }}>
                {children}
              </ol>
            );
          },
          li({ children }) {
            return (
              <li style={{ ...markdownStyle, marginBottom: '8px' }}>
                {children}
              </li>
            );
          },
          table: ({ children }) => (
            <div style={tableWrapperStyle}>
              <table style={tableStyle} className="custom-table">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead style={tableTheadStyle}>{children}</thead>
          ),
          tbody: ({ children }) => (
            <tbody style={tableBodyStyle}>{children}</tbody>
          ),
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => (
            <th style={{ ...cellStyle, backgroundColor: '#90AFC5', color: '#FFF' }}>{children}</th>
          ),
          td: ({ children }) => (
            <td style={cellStyle}>{children}</td>
          )
        }}
      >
        {formattedData}
      </Markdown>

      {/* Renderizado de imagen después del contenido */}
      {img && (
        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => {
              const btn = e.currentTarget.querySelector('.download-btn');
              if (btn) btn.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              const btn = e.currentTarget.querySelector('.download-btn');
              if (btn) btn.style.opacity = 0;
            }}
          >
            {/* Imagen */}
            <img
              src={img.startsWith('data:image') ? img : `data:image/png;base64,${img}`}
              alt="Imagen generada"
              style={{
                maxWidth: '100%',
                borderRadius: '8px',
                display: 'block',
              }}
            />

            {/* Botón de descarga */}
            <Tooltip
              title="Descargar"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#1D3D4D",
                    color: "#FFF",
                    fontFamily: "Montserrat",
                    fontSize: "0.875rem",
                    padding: "8px 12px",
                    borderRadius: "8px",
                  },
                },
                arrow: {
                  sx: {
                    color: "#1D3D4D", // Color de la flecha
                  },
                },
              }}
            >
              <a
                className="download-btn"
                href={img.startsWith('data:image') ? img : `data:image/png;base64,${img}`}
                download="imagen-generada.png"
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  color: 'white',
                  padding: '5px 8px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <DownloadIcon />
              </a>
            </Tooltip>
          </div>
        </div>
      )}

    </div>
  );
};

export default MarkDownText;
